// src/components/Card/Office/OfficeCard.jsx
//
// Restyle siguiendo el patrón ".office" del mockup de Inicio: barra de
// color superior, "sello" con iniciales o icon_url, nombre + ubicación,
// badge de rol (Propietario/Miembro), stats (activos/expedientes/
// miembros/facturado) y avatares del equipo.

import React from 'react';
import { Paper, Box, Typography, Avatar, Chip, IconButton, Tooltip } from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import { useNavigate } from 'react-router-dom';
import { tokens, gradientForName } from '../../../theme/tokens';

const getInitials = (name) => {
  if (!name) return 'OF';
  return name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
};

const currencyFormatter = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const OfficeCard = ({ office, onEdit, onDelete, showActions = false, roleLabel, stats, team = [] }) => {
  const navigate = useNavigate();

  const handleClick = () => navigate(`/office/${office.id}/home`);
  const sealBg = office.color || gradientForName(office.name || '');

  return (
    <Paper
      onClick={handleClick}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        borderRadius: `${tokens.radius.lg}px`,
        p: 3,
        transition: '.18s',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: tokens.colors.line2,
        },
      }}
    >
      {/* barra superior de color */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          backgroundColor: office.color || undefined,
          backgroundImage: office.color ? undefined : tokens.gradient.main,
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          src={office.icon_url || undefined}
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            fontWeight: 800,
            fontSize: '1.1rem',
            backgroundColor: office.color || undefined,
            backgroundImage: office.icon_url || office.color ? undefined : sealBg,
            flexShrink: 0,
          }}
        >
          {!office.icon_url && getInitials(office.name)}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={720} noWrap>
            {office.name}
          </Typography>
          {office.location && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: tokens.colors.muted,
                fontSize: 13,
                mt: 0.3,
              }}
            >
              <LocationOnRoundedIcon sx={{ fontSize: 13 }} />
              <Typography variant="caption" noWrap sx={{ fontSize: 13 }}>
                {office.location}
              </Typography>
            </Box>
          )}
        </Box>

        {roleLabel && (
          <Chip
            label={roleLabel}
            size="small"
            sx={{
              backgroundImage: tokens.gradient.soft,
              color: tokens.colors.indigo,
              fontWeight: 700,
              fontSize: 10.5,
              textTransform: 'uppercase',
            }}
          />
        )}

        <IconButton
          size="small"
          sx={{
            backgroundColor: tokens.colors.panel2,
            color: tokens.colors.muted,
            borderRadius: '12px',
            '&:hover': { backgroundImage: tokens.gradient.main, color: '#08131a' },
          }}
        >
          <ArrowForwardRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {stats && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1.25,
            mt: 2.5,
            pt: 2.25,
            borderTop: `1px solid ${tokens.colors.line}`,
          }}
        >
          {[
            { v: stats.active, l: 'Activos' },
            { v: stats.total, l: 'Expedientes' },
            { v: stats.members, l: 'Miembros' },
            { v: currencyFormatter.format(stats.billed || 0), l: 'Facturado' },
          ].map((s) => (
            <Box key={s.l}>
              <Typography variant="body1" fontWeight={730} sx={{ fontSize: 20, letterSpacing: '-.4px' }}>
                {s.v}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11.5, fontWeight: 600 }}>
                {s.l}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {team.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          {team.slice(0, 4).map((name, i) => (
            <Avatar
              key={i}
              sx={{
                width: 28,
                height: 28,
                fontSize: 10.5,
                fontWeight: 700,
                border: `2px solid ${tokens.colors.panel}`,
                backgroundImage: gradientForName(name),
                marginLeft: i === 0 ? 0 : '-8px',
              }}
            >
              {getInitials(name)}
            </Avatar>
          ))}
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ ml: 1.25, fontSize: 12 }}
          >
            {team.join(', ')}
          </Typography>
        </Box>
      )}

      {showActions && (onEdit || onDelete) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 0.5,
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px solid ${tokens.colors.line}`,
          }}
        >
          {onEdit && (
            <Tooltip title="Editar">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(office.id);
                }}
              >
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(office.id);
                }}
                sx={{ '&:hover': { color: tokens.colors.red } }}
              >
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default OfficeCard;